import React, { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Alert, Button } from 'react-bootstrap';
import { AppState } from '../store/store';
import { clearFeedback, dismissFeedback, getFeedbackActionCallback } from './feedbackReducer';
import './Feedback.scss';

const useNavigationClearFeedback = (message: string | null) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const initialPathRef = useRef<string | null>(null);
  const prevMessageRef = useRef<string | null>(null);

  // Track the path when feedback is first shown (only when message transitions from null to non-null)
  useEffect(() => {
    if (message !== null && prevMessageRef.current === null) {
      initialPathRef.current = location.pathname;
    } else if (message === null) {
      initialPathRef.current = null;
    }
    prevMessageRef.current = message;
  }, [message, location.pathname]);

  // Clear feedback without calling onDismiss if user navigates away manually
  useEffect(() => {
    if (message !== null && initialPathRef.current !== null && location.pathname !== initialPathRef.current) {
      dispatch(clearFeedback());
    }
  }, [location.pathname, message, dispatch]);
};

const NavigationClearFeedbackWrapper = ({ message }: { message: string | null }) => {
  useNavigationClearFeedback(message);
  return null;
};

export const Feedback = () => {
  const dispatch = useDispatch();
  const { message, variant, actionLabel } = useSelector((state: AppState) => state.feedback, shallowEqual);

  useEffect(() => {
    if (message === null) {
      return;
    }
    const timeout = setTimeout(() => {
      dispatch(dismissFeedback());
    }, 3000);
    return () => clearTimeout(timeout);
  }, [message, variant, dispatch]);

  if (message === null) {
    return null;
  }

  const handleActionClick = () => {
    const callback = getFeedbackActionCallback();
    if (callback) {
      callback();
    }
    dispatch(clearFeedback());
  };

  return (
    <>
      <NavigationClearFeedbackWrapper message={message} />
      <div className={`feedback me-4`}>
        <Alert className={`alert ${variant}`} variant={variant} aria-label={message}>
          <span>{message}</span>
          {actionLabel && (
            <Button variant='link' size='sm' className='feedback-action' onClick={handleActionClick}>
              {actionLabel}
            </Button>
          )}
        </Alert>
      </div>
    </>
  );
};

export default Feedback;
